import { useState } from 'react';
import { CardContent, Grid } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import moment from 'moment-timezone';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { QuickEmailDialog } from '../../../../../shared/template/template-editor/quick-email-dialog/quick-email-dialog';
import { Contact } from '../../../../../../services/api-client/contact-api';
import { isAddressEmpty } from '../../../../../layout/slideout/patient-profile-widget/views/comm-preferences/utils/is-address-empty';
import { PhoneType } from '../../../../../layout/slideout/patient-profile-widget/views/general/types';
import { Card, CardTitle, Separator } from '../../patient-profile-page.styles';
import { FamilyMembers } from './family-members-component';
import { InsuranceComponent } from './insurance-component';
import { InsuranceName } from './insurance-name-component';
import { Balance, InfoTitle, InsuranceContainer } from './overview.styles';
import { PhoneComponent } from './phone-component';
import { Credit } from './sections/carecredit/credit';

const renderType = (contact: Contact) => {
  if (contact?.isResponsibleParty) {
    return 'Responsible Party';
  }

  return 'Family Member';
};

const PatientInfo = () => {
  const { contactDetails, careCreditInfo, patientInfo } = usePatientProfile();
  const { recallDate, familyMembers, primBenefitsRemaining, outstandingBalance30Days } = patientInfo;
  const [quickEmailDialogOpen, setQuickEmailDialogOpen] = useState(false);
  const familyMembersList = familyMembers?.filter((member) => member.id !== contactDetails.id) || [];
  const pateintRecallDate = recallDate ? moment(recallDate).format('MM/DD/YYYY') : 'Not to Recall';

  const handleEmailClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setQuickEmailDialogOpen(true);
  };

  return (
    <>
      <Card>
        <CardContent>
          <CardTitle>Patient Info</CardTitle>
          <Separator>General</Separator>
          <Grid container>
            <Grid item xs={6}>
              <InfoTitle>Date of Birth</InfoTitle>
              {moment(contactDetails.dateOfBirth).format('MM/DD/YYYY')}
              <InfoTitle>Phone Numbers</InfoTitle>
              <PhoneComponent phoneNumber={contactDetails.cellPhone} phoneType={PhoneType.Mobile} />
              <PhoneComponent phoneNumber={contactDetails.homePhone} phoneType={PhoneType.Home} />
              <PhoneComponent phoneNumber={contactDetails.workPhone} phoneType={PhoneType.Work} />
              <InfoTitle>Address</InfoTitle>
              {!isAddressEmpty(contactDetails.address) ? (
                <>
                  {contactDetails.address?.address1} {contactDetails.address?.address2} {contactDetails.address?.city},{' '}
                  {contactDetails.address?.state} {contactDetails.address?.zip}
                </>
              ) : (
                '-'
              )}
              <InfoTitle>Email</InfoTitle>
              {contactDetails.email ? (
                <a href="#" onClick={handleEmailClick}>
                  {contactDetails.email}
                </a>
              ) : (
                '-'
              )}
            </Grid>
            <Grid item xs={6}>
              <InfoTitle>Role</InfoTitle>
              {renderType(contactDetails)}
              <InfoTitle>Family</InfoTitle>
              <FamilyMembers familyMembers={familyMembersList} />
              <InfoTitle>Recall Date</InfoTitle>
              {pateintRecallDate}
              {careCreditInfo && <Credit careCreditInfo={careCreditInfo} />}
            </Grid>
          </Grid>
          <Separator>Billing</Separator>
          <Grid container>
            <Grid item xs={6}>
              <InfoTitle>Primary Insurance</InfoTitle>
              <InsuranceName type="primary" />
              <InsuranceComponent type="primary" />
              <InfoTitle>Primary Insurance Remaining</InfoTitle>
              {primBenefitsRemaining || '-'}
            </Grid>
            <Grid item xs={6}>
              <InfoTitle>Secondary Insurance</InfoTitle>
              <InsuranceName type="secondary" />
              <InsuranceComponent type="secondary" />
              <Balance>
                <AttachMoneyIcon />
                30 Day - ${outstandingBalance30Days || 0}
              </Balance>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {contactDetails && contactDetails.location && contactDetails.email && (
        <QuickEmailDialog
          open={quickEmailDialogOpen}
          patientId={contactDetails.id}
          locationId={contactDetails.location.id}
          fromName={contactDetails.location.name}
          title={`Send Email to ${contactDetails.firstName} ${contactDetails.lastName}`}
          onSend={() => setQuickEmailDialogOpen(false)}
          onClose={() => setQuickEmailDialogOpen(false)}
        />
      )}
    </>
  );
};

export default PatientInfo;
